import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import styled from 'styled-components';

import { createForm } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { Page } from '@components/page-containers/page-container';
import { RouteErrorBoundary } from '@components/route-error-boundary';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { hourList, minuteList } from '@utils/number-arrays';

import { TimeSavedData } from 'api/journeys/requests';
import { OutOfCreditsBanner } from 'components/out-of-credits-banner';

import { useFeature } from './hooks';

type RouteList = readonly {
  name: string;
  path: string;
  page: JSX.Element;
  hideNavbar?: boolean;
}[];

type RoutePaths<T extends RouteList> = {
  [K in T[number] as K['name']]: K['path'];
};

type RouteData = {
  routes: RouteList;
  prefix?: string;
};

const extractRoutesFromData = <T extends RouteData>(data: T): RoutePaths<T['routes']> =>
  data.routes.reduce(
    (obj, route) => ({ ...obj, [route.name]: (data.prefix ?? '/') + route.path }),
    {} as RoutePaths<T['routes']>
  );

export const getRoutePaths = <T extends RouteData[]>(data: T): RoutePaths<T[number]['routes']> =>
  data.reduce(
    (obj, data) => ({ ...obj, ...extractRoutesFromData(data) }),
    {} as RoutePaths<T[number]['routes']>
  );

type RouteMapperProps = {
  routes: RouteList;
  defaultRoute?: RouteList[number]['path'];
};

export const RouteMapper = ({ routes, defaultRoute }: RouteMapperProps) => {
  const location = useLocation();

  return (
    <Page>
      <OutOfCreditsBanner />
      <Routes>
        {routes.map((route) => (
          <Route
            key={route.name}
            path={route.path}
            element={route.page}
            errorElement={<RouteErrorBoundary />}
          />
        ))}
        {defaultRoute ? (
          <Route
            path="*"
            element={<Navigate to={defaultRoute} state={{ from: location }} replace />}
            errorElement={<RouteErrorBoundary />}
          />
        ) : null}
      </Routes>
    </Page>
  );
};

const Form = createForm<TimeSavedData>();

export const TimeSavedContainer = (props: Partial<TimeSavedData>) => {
  const { days, hours, minutes } = props;
  const { isEnabled: hasTimeSavedEnabled } = useFeature('FEATURE_TIME_SAVED');
  const { t } = useScopedTranslate(locale);

  return hasTimeSavedEnabled ? (
    <>
      <HideContainer title={t('TimeSaved')} defaultClosed>
        <Stack space={4}>
          <FormGroupContainer>
            <Form.TextField
              id="days"
              label={t('days')}
              defaultValue={days?.toString() ?? '0'}
              validators={['number']}
            />
            <Form.Select
              id="hours"
              label={t('hours')}
              options={hourList}
              placeholder=""
              defaultValue={hours?.toString() ?? '0'}
              isClearable={false}
            />
            <Form.Select
              id="minutes"
              label={t('minutes')}
              options={minuteList}
              placeholder=""
              defaultValue={minutes?.toString() ?? '0'}
              isClearable={false}
            />
          </FormGroupContainer>
          <Text text={t('timeSavedInfo')} font="semibold14" />
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
    </>
  ) : null;
};

export const FormGroupContainer = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
})({
  flexDirection: 'column',
  '> *': {
    flex: '1 1',
  },
  [theme.mediaBreakpoint.md]: {
    flexDirection: 'row',
  },
});

const locale = {
  en: {
    TimeSaved: 'Time saved',
    timeSavedInfo: 'How much time will this action save you?',
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
  },
};
